import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router';
import Icon from '../app/components/Icon';
import { AppContext } from '../app/lib/AppContext';
import { createApi } from '../app/lib/api';
import FeedbackPage from './FeedbackPage';
import AnnouncementsPage from './AnnouncementsPage';
import TakeoutDishesPage from './TakeoutDishesPage';
import UsersPage from './UsersPage';
import UserDetailPage from './user/UserDetailPage';
import FoodsPage from './FoodsPage';
import AiUsagePage from './AiUsagePage';

// The admin panel, on the website only (/admin): never shipped as part of the phone app's screens.
// Rails only serves it to a signed-in Admin (its own login, with an authenticator code), and the API checks
// that session on every call. After 30 idle minutes the session ends and the next call goes back to the login.
const SECTIONS = [
  { to: '/admin', label: 'Góp ý người dùng', icon: 'pencil', end: true },
  { to: '/admin/announcements', label: 'Thông báo tính năng', icon: 'sparkles' },
  { to: '/admin/takeout-dishes', label: 'Món đặt ngoài đã học', icon: 'bag' },
  { to: '/admin/users', label: 'Người dùng', icon: 'user' },
  { to: '/admin/foods', label: 'Thực phẩm', icon: 'bowl', badge: true },
  { to: '/admin/ai-usage', label: 'Sử dụng AI', icon: 'stats' },
];
// A 401 means the admin session ended (30 idle minutes, or signed out elsewhere): back to the login.
const toLogin = (e) => {
  if (e.status === 401) window.location.assign('/admin/login');
  throw e;
};
const api = Object.fromEntries(Object.entries(createApi('/api')).map(([verb, call]) => [verb, (...args) => call(...args).catch(toLogin)]));
const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

export default function AdminApp({ name }) {
  const context = useMemo(() => ({ api, admin: true, base: '', editable: false, goals: {}, membership: null }), []);
  // How many ingredient names wait in "Cần xem lại", shown next to it.
  const [toReview, setToReview] = useState(0);
  useEffect(() => { api.get('/admin/food_review_flags?status=open').then((d) => setToReview(d.counts.open || 0)).catch(() => {}); }, []);
  const link = ({ isActive }) => `flex min-h-11 items-center gap-3 whitespace-nowrap rounded-[12px] px-3 text-[15px] font-semibold
    ${isActive ? 'bg-base-100 text-base-content shadow-sm' : 'text-muted hover:bg-base-100/60'}`;

  return (
    <AppContext.Provider value={context}>
      <BrowserRouter>
        <div data-theme="macro" className="min-h-dvh bg-base-200 text-base-content">
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 md:flex-row md:gap-8 md:px-8 md:py-8">
            <aside className="flex shrink-0 flex-col gap-3 md:sticky md:top-8 md:h-fit md:w-60">
              <div className="flex items-center justify-between md:block">
                <div>
                  <p className="font-display text-[22px]">Macro Mate</p>
                  <p className="text-sm text-muted">Quản trị{name ? ` · ${name}` : ''}</p>
                </div>
                <a href="/me" className="btn btn-ghost btn-sm h-10 md:hidden">Về app</a>
              </div>
              <nav aria-label="Quản trị" className="-mx-1 flex gap-1 overflow-x-auto px-1 md:flex-col md:overflow-visible">
                {SECTIONS.map((s) => (
                  <NavLink key={s.to} to={s.to} end={s.end} className={link}>
                    <Icon name={s.icon} className="size-5" /> {s.label}
                    {s.badge && toReview > 0 && <span className="rounded-full bg-error px-2 text-xs font-bold text-white">{toReview}</span>}
                  </NavLink>
                ))}
              </nav>
              <a href="/me" className="hidden min-h-11 items-center gap-2 px-3 text-sm font-semibold text-brand-text md:flex">
                <Icon name="chevronLeft" className="size-4" /> Về app
              </a>
              <form method="post" action="/admin/logout">
                <input type="hidden" name="_method" value="delete" />
                <input type="hidden" name="authenticity_token" value={csrfToken() || ''} />
                <button type="submit" className="min-h-11 px-3 text-sm font-semibold text-muted hover:text-base-content">Đăng xuất quản trị</button>
              </form>
            </aside>
            <main className="min-w-0 flex-1 md:max-w-3xl">
              <Routes>
                <Route path="/admin" element={<FeedbackPage />} />
                <Route path="/admin/announcements" element={<AnnouncementsPage />} />
                <Route path="/admin/takeout-dishes" element={<TakeoutDishesPage />} />
                <Route path="/admin/users" element={<UsersPage />} />
                <Route path="/admin/users/:id" element={<UserDetailPage />} />
                <Route path="/admin/foods" element={<FoodsPage />} />
                <Route path="/admin/food-review" element={<Navigate to="/admin/foods?tab=review" replace />} />
                <Route path="/admin/coaches" element={<Navigate to="/admin/users" replace />} />
                <Route path="/admin/ai-usage" element={<AiUsagePage />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Routes>
            </main>
          </div>
        </div>
      </BrowserRouter>
    </AppContext.Provider>
  );
}
